/** Ad attribution params forwarded through login to backend Segment events. */
export interface AdAttribution {
  li_fat_id?: string
  twclid?: string
  rdt_cid?: string
  fbclid?: string
  gclid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
}

export const AD_ATTRIBUTION_COOKIE = 'replay-ad-attribution'

const AD_ATTR_KEYS = [
  'li_fat_id',
  'twclid',
  'rdt_cid',
  'fbclid',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const satisfies readonly (keyof AdAttribution)[]

// 90 days to match the landing-page cookie + the ad platforms' CVR window; refreshing the shared
// .replay.io cookie on a qa.replay.io visit must not shorten a 90-day landing-page capture.
const COOKIE_MAX_AGE_DAYS = 90

function readAttributionFromSearch(search: string): AdAttribution | null {
  const params = new URLSearchParams(search)
  const out: AdAttribution = {}
  let any = false
  for (const key of AD_ATTR_KEYS) {
    const value = params.get(key)?.trim()
    if (value) {
      out[key] = value
      any = true
    }
  }
  return any ? out : null
}

function readAttributionCookie(): AdAttribution | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${AD_ATTRIBUTION_COOKIE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`),
  )
  if (!match?.[1]) return null
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1])) as AdAttribution
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

// Scope the attribution cookie to the whole replay.io family so a UTM captured on a replay.io
// marketing landing persists to qa.replay.io (and any other subdomain). Host-only anywhere else
// (localhost, *.netlify.app previews), where a shared parent domain would be wrong or rejected.
function attributionCookieDomain(): string {
  try {
    const host = window.location.hostname
    if (host === 'replay.io' || host.endsWith('.replay.io')) return '.replay.io'
  } catch {
    // window/location unavailable — fall through to host-only.
  }
  return ''
}

function writeAttributionCookie(attribution: AdAttribution): void {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60
  const value = encodeURIComponent(JSON.stringify(attribution))
  const domain = attributionCookieDomain()
  if (domain) {
    // Clear any older host-only cookie of the same name first so a single, unambiguous family-wide
    // cookie remains (two same-named cookies would read back ambiguously).
    document.cookie = `${AD_ATTRIBUTION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
    document.cookie = `${AD_ATTRIBUTION_COOKIE}=${value}; Path=/; Domain=${domain}; Max-Age=${maxAge}; SameSite=Lax`
  } else {
    document.cookie = `${AD_ATTRIBUTION_COOKIE}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax`
  }
}

/** Persist landing-page UTM / click-id params before the user signs in. */
export function captureAdAttribution(): void {
  // first-touch: once attribution is captured (here, or on a replay.io landing via the shared
  // .replay.io cookie) keep it and just refresh Max-Age. a later CTA carrying utm_source=homepage
  // must not clobber the real inbound ad source.
  const existing = readAttributionCookie()
  if (existing) {
    writeAttributionCookie(existing)
    return
  }
  const fromUrl = readAttributionFromSearch(window.location.search)
  if (fromUrl) writeAttributionCookie(fromUrl)
}

/** Remove attribution cookie after login (server-side signup already consumed it). */
export function clearAdAttributionCookie(): void {
  document.cookie = `${AD_ATTRIBUTION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
  // Also clear the family-wide (.replay.io) variant this browser may hold from a replay.io landing.
  const domain = attributionCookieDomain()
  if (domain) document.cookie = `${AD_ATTRIBUTION_COOKIE}=; Path=/; Domain=${domain}; Max-Age=0; SameSite=Lax`
}
